import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { t } from '../../i18n/index.js';
import { authClient } from '../../platform/auth.js';
import { keys, queryClient, useConfig } from '../../platform/queries.js';
import { AuthLayout, Button, Field, Input, Notice } from '../../ui/ui.js';
import { signInWithPasskey } from '../../platform/passkey.js';

/** Anmeldung: Magic Link als Standard (wenn E-Mail eingerichtet), Passwort und Passkey als Alternativen */
export function LoginPage({ redirect }: { redirect?: string }) {
  const navigate = useNavigate();
  const config = useConfig();
  const mail = config.data?.mailEnabled ?? false;
  // Erst mit geladener Konfiguration steht fest, ob „Per Link“ oder „Mit Passwort“ gilt – vorher nichts anbieten,
  // was beim Laden umspringt (sonst ginge ein schon getipptes Passwort als Link-Anforderung raus)
  const ready = config.data !== undefined || config.isError;
  const [mode, setMode] = useState<'link' | 'password'>('link');
  const effective = mail ? mode : 'password';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const target = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/';

  async function done() {
    await queryClient.invalidateQueries({ queryKey: keys.me });
    await navigate({ to: target });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    if (effective === 'link') {
      const r = await authClient.signIn.magicLink({ email, callbackURL: target });
      setBusy(false);
      if (r.error) setError(r.error.message ?? t('error.generic'));
      else setSent(true);
      return;
    }
    const r = await authClient.signIn.email({ email, password });
    setBusy(false);
    if (r.error) setError(r.error.status === 401 ? t('login.failed') : (r.error.message ?? t('error.generic')));
    else await done();
  }

  async function passkey() {
    setError(null);
    const r = await signInWithPasskey().catch((e: unknown) => ({ error: { message: e instanceof Error ? e.message : null } }));
    if (r?.error) setError(r.error.message ?? t('error.generic'));
    else await done();
  }

  return (
    <AuthLayout title={t('login.title')}>
      <form onSubmit={submit} className="space-y-4">
        {mail && (
          <div role="tablist" className="grid grid-cols-2 gap-1 rounded-lg bg-zinc-100 p-1 dark:bg-zinc-800">
            {(['link', 'password'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                onClick={() => setMode(m)}
                className={`min-h-10 rounded-md text-sm ${mode === m ? 'bg-white font-medium shadow-sm dark:bg-zinc-900' : 'text-zinc-600 dark:text-zinc-400'}`}
              >
                {m === 'link' ? t('login.withLink') : t('login.withPassword')}
              </button>
            ))}
          </div>
        )}
        {error && <Notice kind="error">{error}</Notice>}
        {sent ? (
          <Notice kind="success">{t('login.linkSent', { email })}</Notice>
        ) : (
          <>
            <Field label={t('common.email')}>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username webauthn" required autoFocus />
            </Field>
            {ready && effective === 'password' && (
              <Field label={t('common.password')}>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
              </Field>
            )}
            <Button type="submit" variant="primary" busy={busy} disabled={!ready} className="w-full">
              {effective === 'link' ? t('login.sendLink') : t('login.submit')}
            </Button>
          </>
        )}
        {config.data?.passkeys && (
          <Button type="button" variant="ghost" className="w-full" onClick={passkey}>
            <KeyRound className="size-4" aria-hidden /> {t('login.passkey')}
          </Button>
        )}
      </form>
    </AuthLayout>
  );
}
